import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription, filter } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { Category, StorageService } from '../../../services/storage.service';

type MobileTab = 'home' | 'search' | 'breaking' | 'portfolio' | '';

@Component({
  selector: 'app-footer',
  imports: [CommonModule, RouterModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css'
})
export class FooterComponent implements OnInit, OnDestroy {
  currentYear: number = new Date().getFullYear();
  currentUrl: string = '/';
  usdcBalance: number = 0;
  isMoreSheetOpen: boolean = false;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private router: Router,
    private storage: StorageService,
    private cdr: ChangeDetectorRef,
    public auth: AuthService
  ) { }

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.subscriptions.add(
      this.router
        .events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: any) => {
          this.currentUrl = event.urlAfterRedirects || event.url;
          this.isMoreSheetOpen = false;
          if (!this.isHomePage) {
            // Search belongs to the home page; leaving it drops the tab.
            this.storage.setSearchTabActive(false);
          }
        })
    );

    // Same USDC balance the header shows, kept in sync by the global portfolio stream.
    this.subscriptions.add(
      this.auth
        .portfolioSummary$
        .subscribe((res: any) => {
          if (!res) {
            this.usdcBalance = 0;
            this.cdr.detectChanges();
            return;
          }
          this.usdcBalance = Number(res.cash_balance ?? res.data?.cash_balance ?? this.usdcBalance) || 0;
          this.cdr.detectChanges();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * The tab that should render in its active state for the current page.
   * @returns {MobileTab} The active tab, or an empty string when none matches.
   */
  get activeTab(): MobileTab {
    if (this.currentUrl.startsWith('/portfolio')) {
      return 'portfolio';
    }
    // Stays lit once Search was tapped, including after the search is over.
    if (this.isHomePage && this.storage.isSearchTabActive()) {
      return 'search';
    }
    if (this.isHomePage) {
      return this.isBreakingSelected ? 'breaking' : 'home';
    }
    return '';
  }

  get isHomePage(): boolean {
    return this.currentUrl === '/' || this.currentUrl.startsWith('/?');
  }

  /**
   * The API category backing the "Breaking" tab, when the API returns one.
   * @returns {Category | undefined} The matching category.
   */
  private get breakingCategory(): Category | undefined {
    return this.storage
      .getCategoryData()
      .find((category: Category) => category.name?.trim().toLowerCase() === 'breaking');
  }

  private get isBreakingSelected(): boolean {
    const breaking: Category | undefined = this.breakingCategory;
    return !!breaking && this.storage.getSelectedCategory() === breaking.id;
  }

  /**
   * Sends a category selection through the storage service so the category nav
   * bar (and through it the home market list) reacts to it, navigating to the
   * home page first when the tab bar was tapped from another page.
   * @param {string} category - The category id to select.
   * @param {string} option - 'default' for "All", 'dynamic' for an API category.
   * @returns {void}
   */
  private applyCategory(category: string, option: string): void {
    this.storage.setSelectedCategory(category);

    if (this.isHomePage) {
      this.storage.requestCategorySelection(category, option);
      return;
    }
    // The nav bar restores the stored category itself once the home page mounts.
    this.router.navigate(['/']);
  }

  /**
   * Home tab: goes back to the unfiltered home page.
   * @returns {void}
   */
  goHome(): void {
    this.closeMoreSheet();
    this.storage.setSearchTabActive(false);
    this.applyCategory('all', 'default');
  }

  /**
   * Search tab: opens the home page and focuses its market search input.
   * @returns {void}
   */
  openSearch(): void {
    this.closeMoreSheet();
    this.storage.setSearchTabActive(true);
    this.storage.requestSearchFocus();
    if (!this.isHomePage) {
      this.router.navigate(['/']);
    }
  }

  /**
   * Breaking tab: filters the home page by the "Breaking" category.
   * @returns {void}
   */
  openBreaking(): void {
    this.closeMoreSheet();
    this.storage.setSearchTabActive(false);
    const breaking: Category | undefined = this.breakingCategory;
    if (!breaking) {
      // No such category came back from the API — fall back to the home page.
      this.applyCategory('all', 'default');
      return;
    }
    this.applyCategory(breaking.id, 'dynamic');
  }

  /**
   * Portfolio tab, shown in place of "More" once the user is signed in.
   * @returns {void}
   */
  goToPortfolio(): void {
    this.closeMoreSheet();
    this.router.navigate(['/portfolio']);
  }

  toggleMoreSheet(): void {
    this.isMoreSheetOpen = !this.isMoreSheetOpen;
  }

  closeMoreSheet(): void {
    this.isMoreSheetOpen = false;
  }

  /**
   * Opens the login modal owned by the header.
   * @returns {void}
   */
  openLogin(): void {
    this.closeMoreSheet();
    this.auth.sendOperationSignal('login');
  }
}
